/**
 * New console — the guided tour.
 *
 * Points at the real controls on the real screen: a spotlight ring around the thing
 * being described, and a card beside it. It runs once by itself the first time
 * somebody opens the console, and can be replayed from Getting started or the account
 * menu afterwards.
 *
 * Three things it has to survive, all of which come up immediately in practice:
 *
 *  - **A step's target may not exist.** The rail is gated by capability, so a clinic
 *    admin has no Diagnose link. A step whose target is missing is skipped rather than
 *    pointing at the top-left corner of the page — a tour that highlights nothing is
 *    worse than one step shorter.
 *  - **The page moves.** Scrolling, resizing, the rail collapsing: the ring is
 *    measured from the live element on every frame that matters, not once at the start.
 *  - **The card must not cover its own target.** It goes below by default and flips
 *    above when there is no room, and is clamped inside the viewport either way.
 *
 * Deliberately not a modal: the scrim is there to dim, but nothing traps focus,
 * because the point is to look at the page rather than at the tour.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconX, IconChevL, IconChevR, IconCheck } from './Icons';

/* Every selector here is a real control in the shell or on Home. `sel` is tried in
   order, so a step can name a fallback when a screen renders one of two things. */
const TOUR_STEPS = [
  {
    sel: ['.nt-rail'],
    title: 'Everything lives here',
    body: 'Work at the top, the portfolio in the middle, system and account at the bottom. '
      + 'What you can see depends on your role — a screen missing from this rail is one your '
      + 'account does not hold the permission for.',
    /* On a phone the rail is a hidden drawer with no button left to open it — the menu
       button moved into the bottom bar's own More sheet. Point at that bar instead of a
       rail sitting off-canvas. */
    phone: {
      sel: ['.nt-dock'],
      body: 'Home, Leads and the rest of what you use daily sit in this bar. What you can '
        + 'reach depends on your role, and More opens everything else it holds.'
    }
  },
  {
    sel: ['.nt-nav-item[href="/triage"]'],
    title: 'Triage is the foundation',
    body: 'Until a lead has an outcome recorded, every number above it is an estimate. This '
      + 'queue does one lead at a time, so clearing it is quick. The badge counts what is '
      + 'waiting.',
    phone: {
      sel: ['.nt-dock-more'],
      body: 'Until a lead has an outcome recorded, every number above it is an estimate. On '
        + 'a phone it sits behind More, with everything else the bar has no room for. This '
        + 'queue does one lead at a time, so clearing it is quick.'
    }
  },
  {
    sel: ['.nt-clinic-pick'],
    title: 'One clinic, or all of them',
    body: 'This scopes every screen at once. It opens on all clinics, so the figures are the '
      + 'account’s until you narrow them on purpose.'
  },
  {
    sel: ['.nt-kbar', '.nt-top .nt-icon-btn'],
    title: 'Jump anywhere',
    body: 'Ctrl+K opens the command palette. Search pages and clinics without crossing the '
      + 'rail — on a console this size it is the difference between two clicks and eight.',
    /* Nothing on a phone does what this step describes: no keyboard for Ctrl+K, and the
       search bar it opens is hidden there too. Skipped rather than mislabelled. */
    phone: { sel: [] }
  },
  {
    sel: ['.nt-nav-item[href="/notifications"]', '.nt-nav-item[href="/leads"]'],
    title: 'Alerts find you',
    body: 'Clinics that stop taking bookings, feeds that stop matching, integrations missing '
      + 'their keys. The count is what you have not opened yet — scrolling the list never '
      + 'clears it.',
    /* The bell stays in the topbar on a phone; Leads is a bottom-bar tab for a role
       with no bell (notify.log is superuser-only). */
    phone: { sel: ['.nt-icon-btn.nt-bell', '.nt-dock-tab[href="/leads"]'] }
  },
  {
    sel: ['.nt-who'],
    title: 'Your account and how it looks',
    body: 'Your profile, password and email, and Display and accessibility — text size, '
      + 'density, contrast, reduced motion. Going back to the classic console is here too.',
    /* `.nt-who` is the rail's own account row, off-canvas on a phone; the topbar's
       avatar button opens the same panel. `button.nt-avatar` picks that one out — a
       plain `.nt-avatar` would match the rail's copy first, since it comes first in
       the DOM. */
    phone: { sel: ['button.nt-avatar'] }
  }
];

export const TOUR_SEEN = 'nt.tour.seen';

const PAD = 6;
const CARD_W = 340;
const CARD_H = 210;

const find = (sels) => {
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  for (let i = 0; i < sels.length; i += 1) {
    const n = document.querySelector(sels[i]);
    if (n && n.getBoundingClientRect) {
      const r = n.getBoundingClientRect();
      /* A node with no box, or one pushed off the visible page — the rail's drawer
         closed on a phone, say — is as absent as no node at all. Counting it as found
         drew the ring off-screen instead of moving on to the next real target. */
      if (r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 && r.left < vw && r.top < vh) return n;
    }
  }
  return null;
};

const Tour = ({ onClose }) => {
  /* Only the steps whose target is actually on the page, worked out once on open. A
     phone reaches the console through the bottom bar rather than the rail — see
     NewAppLayout's `hasDock` breakpoint, matched here — so a step naming a rail-only
     control swaps in its phone counterpart first. */
  const steps = useMemo(() => {
    const onPhone = (window.innerWidth || 0) < 768;
    return TOUR_STEPS
      .map((s) => (onPhone && s.phone ? { ...s, ...s.phone } : s))
      .filter((s) => !!find(s.sel));
  }, []);
  const [i, setI] = useState(0);
  const [box, setBox] = useState(null);

  const step = steps[i];
  const last = i === steps.length - 1;

  /* Measured from the live element, and re-measured whenever the page can move. */
  const measure = useCallback(() => {
    if (!step) return;
    const el = find(step.sel);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    setBox({ top: r.top - PAD, left: r.left - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 });
  }, [step]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const finish = useCallback(() => {
    try { window.localStorage.setItem(TOUR_SEEN, '1'); } catch (e) { /* not fatal */ }
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); finish(); }
      if (e.key === 'ArrowRight') setI((n) => Math.min(steps.length - 1, n + 1));
      if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1));
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [finish, steps.length]);

  if (!step || !box) return null;

  /* Below the target, flipped above when the bottom of the screen is closer than the
     card is tall, and clamped so it never hangs off either edge. */
  const vh = window.innerHeight || 800;
  const vw = window.innerWidth || 1200;
  const below = box.top + box.h + 12;
  const top = below + CARD_H > vh ? Math.max(12, box.top - CARD_H - 12) : below;
  const left = Math.min(Math.max(12, box.left), Math.max(12, vw - CARD_W - 12));

  const host = document.querySelector('.nt') || document.body;

  return createPortal(
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="nt-tour-scrim" onClick={finish} />
      <div
        className="nt-tour-ring"
        style={{ top: box.top, left: box.left, width: box.w, height: box.h }}
      />
      <div
        className="nt-tour-card"
        role="dialog"
        aria-label="Guided tour"
        style={{ top, left }}
      >
        <div className="nt-row nt-nowrap">
          <span className="nt-eyebrow">Step {i + 1} of {steps.length}</span>
          <span className="nt-spacer" />
          <button type="button" className="nt-icon-btn" onClick={finish} aria-label="Close the tour">
            <IconX />
          </button>
        </div>

        <h4>{step.title}</h4>
        <p>{step.body}</p>

        <div className="nt-row" style={{ marginTop: 14, flexWrap: 'nowrap' }}>
          <div className="nt-tour-dots">
            {steps.map((s, n) => (
              <i key={s.title} className={n === i ? 'on' : ''} />
            ))}
          </div>
          <span className="nt-spacer" />
          {i > 0 ? (
            <button type="button" className="nt-btn xs" onClick={() => setI(i - 1)}>
              <IconChevL />Back
            </button>
          ) : (
            <button type="button" className="nt-btn xs ghost" onClick={finish}>Skip</button>
          )}
          <button
            type="button"
            className="nt-btn xs primary"
            onClick={() => (last ? finish() : setI(i + 1))}
          >
            {last ? <><IconCheck />Finish</> : <>Next<IconChevR /></>}
          </button>
        </div>
      </div>
    </>,
    host
  );
};

export default Tour;
